import classNames from 'classnames';
import { memo, useCallback, useEffect, useState } from 'react';

import { ImgixImage } from '@air/component-imgix-image';

import { getCloudFrontUrlFromImgixUrl } from '~/constants/cloudfront';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useClip } from '~/swr-hooks/clips/useClip';

export type ClipSearchProps = {
  clipId: string;
};

export const ClipSearch = memo(({ clipId }: ClipSearchProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: clip } = useClip(
    { clipId, workspaceId: currentWorkspace?.id || '' },
    { enabled: !!clipId && !!currentWorkspace?.id },
  );
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (clipId) {
      setImageLoaded(false);
    }
  }, [clipId]);

  const onImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div
      data-testid="CLIP_SEARCH"
      className="pointer-events-none flex min-w-0 max-w-[calc(100%-64px)] items-center gap-1.5 overflow-hidden rounded-4 bg-grey-4 pr-1.5 font-medium text-12 text-grey-12"
    >
      {clip?.assets?.image && (
        <ImgixImage
          className={classNames(
            'h-6 w-6 flex-shrink-0 rounded-4 object-cover transition-opacity duration-300 ease-out [image-rendering:smooth]',
            imageLoaded ? 'opacity-100' : 'opacity-0',
          )}
          alt={clip.title || clip.importedName || ''}
          fallbackUrl={getCloudFrontUrlFromImgixUrl(clip.assets.image)}
          src={clip.assets.image}
          width={24}
          height={24}
          onLoad={onImageLoad}
          imgixParams={{
            fit: 'crop',
            cs: 'origin',
          }}
        />
      )}
      <span className="min-w-0 truncate">{clip?.title || clip?.importedName || ''}</span>
    </div>
  );
});

ClipSearch.displayName = 'ClipSearch';
